import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchCampaigns,
  fetchCampaign,
  fetchCampaignMessages,
  createCampaign,
  updateCampaign,
  sendCampaignTestMessage,
  uploadMedia,
  fetchAbTestMetrics,
  fetchCampaignAnalytics,
} from '../WhatsApp-Marketing/shared/whatsappApi.js';

// Server state for the WhatsApp Campaigns page, the campaign composer, the
// media attachment control and the campaign metric drawer.

const asMessage = (error, fallback) => error?.message || fallback;

export const loadCampaigns = createAsyncThunk(
  'whatsappCampaigns/loadCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const { campaigns } = await fetchCampaigns();
      return campaigns;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load campaigns'));
    }
  }
);

export const loadCampaign = createAsyncThunk(
  'whatsappCampaigns/loadCampaign',
  async (id, { rejectWithValue }) => {
    try {
      return await fetchCampaign(id);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load campaign'));
    }
  }
);

export const loadCampaignMessages = createAsyncThunk(
  'whatsappCampaigns/loadCampaignMessages',
  async (id, { rejectWithValue }) => {
    try {
      const { messages } = await fetchCampaignMessages(id);
      return messages;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load campaign messages'));
    }
  }
);

export const addCampaign = createAsyncThunk(
  'whatsappCampaigns/addCampaign',
  async (data, { rejectWithValue }) => {
    try {
      return await createCampaign(data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to create campaign'));
    }
  }
);

export const editCampaign = createAsyncThunk(
  'whatsappCampaigns/editCampaign',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateCampaign(id, data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to update campaign'));
    }
  }
);

export const sendTestMessage = createAsyncThunk(
  'whatsappCampaigns/sendTestMessage',
  async ({ id, phones }, { rejectWithValue }) => {
    try {
      return await sendCampaignTestMessage(id, phones);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to send test message'));
    }
  }
);

export const uploadCampaignMedia = createAsyncThunk(
  'whatsappCampaigns/uploadMedia',
  async (file, { rejectWithValue }) => {
    try {
      return await uploadMedia(file);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to upload media'));
    }
  }
);

export const loadAbTestMetrics = createAsyncThunk(
  'whatsappCampaigns/loadAbTestMetrics',
  async (campaignId, { rejectWithValue }) => {
    try {
      return await fetchAbTestMetrics(campaignId);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load A/B test metrics'));
    }
  }
);

export const loadCampaignAnalytics = createAsyncThunk(
  'whatsappCampaigns/loadCampaignAnalytics',
  async ({ id, eventType, opts }, { rejectWithValue }) => {
    try {
      return await fetchCampaignAnalytics(id, eventType, opts);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load campaign analytics'));
    }
  }
);

const initialState = {
  campaigns: [],
  loading: true,
  saving: false,
  // Keyed by campaign id so opening one detail row does not clear another.
  messagesByCampaign: {},
  messagesLoading: false,
  activeCampaign: null,
  abTestMetrics: null,
  error: null,
};

const whatsappCampaignsSlice = createSlice({
  name: 'whatsappCampaigns',
  initialState,
  reducers: {
    clearActiveCampaign: (state) => {
      state.activeCampaign = null;
    },
    clearCampaignsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload;
      })
      .addCase(loadCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Only a failed list load: the page shows it instead of an empty list. Save/delete
        // failures are reported where they happen, not as "couldn't load".
        state.loadError = action.payload;
      })

      .addCase(loadCampaign.fulfilled, (state, action) => {
        state.activeCampaign = action.payload;
      })

      .addCase(loadCampaignMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(loadCampaignMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.messagesByCampaign[String(action.meta.arg)] = action.payload;
      })
      .addCase(loadCampaignMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.error = action.payload;
      })

      .addCase(loadAbTestMetrics.fulfilled, (state, action) => {
        state.abTestMetrics = action.payload;
      });

    for (const thunk of [addCampaign, editCampaign, sendTestMessage]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          state.error = action.payload;
        });
    }
  },
});

export const { clearActiveCampaign, clearCampaignsError } = whatsappCampaignsSlice.actions;

export const selectCampaignMessages = (state, id) =>
  state.whatsappCampaigns.messagesByCampaign[String(id)] || [];

export default whatsappCampaignsSlice.reducer;
